import {
  Blog,
  BlogRecommendation,
  Community,
  CommunityEntry,
  CommunityEntryRes,
  EmptyBlog,
  ErrorResponse,
  GetCustomDomainResponse,
  ImportFileResponse,
  ImportType,
  isErrorResponse,
  Link,
  PostSubscribeState,
  RecommendationItem,
  RecommendationModalItem,
  SubscriberCountResp,
  Subscription,
  SuccessResponse,
  UserAndSubscription,
} from "@types"
import { GetTeamMemberResp } from "@/types/teams"
import customFetch, { APIError } from "./helper"
import { User } from "@/types/users"
import { toast } from "sonner"
import { getUtmCookie } from "util/cookies"
import { PaginatedPublicBlogResponseWithAuthorName } from "@/types/responses/public"
import { getServerOrClientCookies } from "../utils/cookies"
import { removeHttpOrHttpsFromDomain } from "@/util/format"

/**
 * This is the base API endpoint for blogs.
 */
const API = "/blogs"

/**
 * This retrieves the logged-in user's blog. This does NOT fetch any blog
 * they may be impersonating.
 *
 */
export async function getLoggedInUsersBlog(): Promise<Blog> {
  const response = await customFetch<Array<Blog>>(
    API,
    {},
    { ignoreImpersonation: true }
  )
  return response?.[0] || EmptyBlog
}

/**
 * This retrives the blog that the user MAY be impersonating, if they're
 * working on a team. If they aren't working on a team, we just fetch their own blog.
 */
export async function getBlogUserMayBeImpersonating(): Promise<Blog> {
  const response = await customFetch<Array<Blog>>(API)

  const data = response?.[0] || EmptyBlog

  return data
}

/**
 * Add a subscriber to a blog
 * @param email The email of the subscriber to add
 * @param wallet Optional wallet of the subscriber to add
 * @returns The user and subscription data, or an error response
 */
export async function addSubscriberToBlog(
  email?: string,
  wallet?: string
): Promise<UserAndSubscription | ErrorResponse> {
  try {
    const response = await customFetch<UserAndSubscription | ErrorResponse>(
      `${API}/add-subscriber`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          wallet,
        }),
      }
    )

    return response
  } catch (error) {
    console.error("Error adding subscriber to blog:", error)
    return { msg: "Error adding subscriber to blog" }
  }
}

/**
 * Import either blogposts or subscribers from a file
 * @param file The file to import
 * @param type Type of import (e.g. "free" for free subscribers)
 * @param importIntoCommunities Whether to import into communities
 * @param communityIds IDs of communities to import into
 * @returns Job ID or error response
 */
export async function importFile(
  file: File,
  type: ImportType,
  importIntoCommunities: boolean,
  communityIds: string[]
): Promise<ImportFileResponse | ErrorResponse> {
  try {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("importType", type)
    formData.append("importIntoCommunities", importIntoCommunities.toString())
    formData.append("communityIds", communityIds.join(","))

    const response = await customFetch<ImportFileResponse | ErrorResponse>(
      `${API}/import`,
      {
        method: "POST",
        body: formData,
      }
    )

    return response
  } catch (error) {
    console.error("Error importing file:", error)
    return { msg: "Something went wrong importing file.", success: false }
  }
}

/**
 * Unsubscribe multiple subscribers in bulk
 * @param subscriptionIds Array of subscription IDs to unsubscribe
 * @returns Success or error response
 */
export async function unsubscribeSubscriberBulk(
  subscriptionIds: Array<string>
): Promise<true | ErrorResponse> {
  try {
    const response = await customFetch<true | ErrorResponse>(
      `${API}/subscriptions/delete/bulk`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ subscriptionIds }),
      }
    )

    return response
  } catch (error) {
    console.error("Error unsubscribing subscribers in bulk:", error)
    return { msg: "Error unsubscribing subscribers" }
  }
}

/**
 * Get a page of blog subscribers
 * @param blogId The ID of the blog
 * @param pageSize Number of results per page
 * @param offset Offset for pagination
 * @param filter Filter by subscription status
 * @param communityFilter Optional community filter
 * @returns The page of subscribers
 */
export async function getBlogsSubscribersPage(
  blogId: string,
  pageSize: number,
  offset: number = 0,
  filter: Array<"Active" | "Unsubscribed"> = ["Active"],
  communityFilter?: any
): Promise<any | undefined> {
  try {
    const params = new URLSearchParams({
      pageSize: pageSize.toString(),
      offset: offset.toString(),
      filter: filter.join(","),
    })

    if (communityFilter) {
      params.append("communityFilter", communityFilter.id)
    }

    const response = await customFetch<any>(
      `${API}/${blogId}/subscribers?${params.toString()}`,
      {},
      {
        ignoreImpersonation: true,
      }
    )

    return response
  } catch (error) {
    console.error("Error fetching blog subscribers page:", error)
    return undefined
  }
}

/**
 * Search for subscribers by search term
 * @param blogId The ID of the blog
 * @param pageSize Number of results per page
 * @param offset Offset for pagination
 * @param searchTerm Search term to filter by
 * @param filter Filter by subscription status
 * @param communityFilter Optional community filter
 * @returns The page of filtered subscribers
 */
export async function getBlogsSubscribersBySearch(
  blogId: string,
  searchTerm: string,
  pageSize: number,
  offset: number = 0,
  filter: Array<"Active" | "Unsubscribed"> = ["Active"],
  communityFilter?: any
): Promise<any | undefined> {
  try {
    const params = new URLSearchParams({
      pageSize: pageSize.toString(),
      offset: offset.toString(),
      filter: filter.join(","),
    })

    if (communityFilter) {
      params.append("communityFilter", communityFilter.id)
    }

    const response = await customFetch<any>(
      `${API}/${blogId}/subscribers/search/${searchTerm}?${params.toString()}`
    )

    return response
  } catch (error) {
    console.error("Error searching blog subscribers:", error)
    return undefined
  }
}

type BlogData = Blog & {
  id: string
  user: User
}

export async function getBlogBySlug(
  slug: string,
  revalidate?: number
): Promise<BlogData> {
  const options: Record<string, any> = {}

  if (!slug) throw "No slug provided when fetching blog by slug"

  if (revalidate !== undefined) {
    options.next = { revalidate }
  }

  try {
    const response = await customFetch<BlogData>(
      `${API}/${decodeURIComponent(slug)}`,
      options,
      {
        ignoreImpersonation: true,
        bypassCookies: true,
      }
    )

    return response
  } catch (e) {
    APIError.handle404Error(e)
  }
}

/**
 * Get a blog by its ID
 * Uses the special endpoint for the unsubscribe flow that doesn't include user data
 * @param blogId The ID of the blog to retrieve
 * @returns Blog object if successful, ErrorResponse if not
 */
export async function getBlogById(
  blogId: string
): Promise<Blog | ErrorResponse> {
  if (!blogId) throw "No blog ID provided when fetching blog by ID"

  try {
    const response = await customFetch<Blog | ErrorResponse>(
      `${API}/id/${blogId}`,
      {},
      {
        ignoreImpersonation: true,
        bypassCookies: true,
      }
    )

    return response
  } catch (e) {
    console.error("Error fetching blog by ID:", e)
    return { msg: "Failed to fetch blog details" }
  }
}

export async function getTeamMembers(
  blogId: string
): Promise<GetTeamMemberResp[]> {
  const response = await customFetch<GetTeamMemberResp[]>(
    `${API}/${blogId}/teammembers`
  )
  return response
}

export async function getCommunities(
  blogId: string
): Promise<Array<CommunityEntryRes>> {
  const response = await customFetch<Array<CommunityEntryRes>>(
    `${API}/${blogId}/communities`,
    { next: { revalidate: 86400 } } // 1 day.
  )
  return response
}

/**
 * Add a new community to a blog
 * @param community The community to add
 * @param addSubscribersToNewCommunity Whether to add existing subscribers to the new community
 * @returns The response from the API
 */
export async function addCommunity(
  community: Community,
  addSubscribersToNewCommunity: boolean
) {
  try {
    return await customFetch<any>(`${API}/community`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ community, addSubscribersToNewCommunity }),
    })
  } catch (error) {
    // If the response was empty, just return a success
    if (
      error instanceof Error &&
      error.message.includes("Failed to parse JSON")
    ) {
      return { success: true }
    }
    throw error
  }
}

/**
 * Update an existing community
 * @param community The community to update
 * @returns The response from the API
 */
export async function updateCommunity(community: CommunityEntry) {
  try {
    return await customFetch<any>(`${API}/community/${community.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ community }),
    })
  } catch (error) {
    // If the response was empty, just return a success
    if (
      error instanceof Error &&
      error.message.includes("Failed to parse JSON")
    ) {
      return { success: true }
    }
    throw error
  }
}

/**
 * Delete a community
 * @param communityId The ID of the community to delete
 * @returns The response from the API
 */
export async function deleteCommunity(communityId: string) {
  try {
    return await customFetch<any>(`${API}/community/${communityId}`, {
      method: "DELETE",
    })
  } catch (error) {
    // If the response was empty, just return a success
    if (
      error instanceof Error &&
      error.message.includes("Failed to parse JSON")
    ) {
      return { success: true }
    }
    throw error
  }
}

/**
 * Used when a subscriber is organically subscribing to a blog
 * (e.g. a writer is NOT adding them manually to their blog).
 *
 * NOTE! This is a very barebones implementation that should be updated to
 * reflect what subscribeUserToBlog was doing in the old nextJS directory
 * (with custom domain cookies, etc.) once we migrate that functionality over.
 */
export async function subscribeUserToBlog({
  lowercaseBlogUrl,
  blogId,
  referrer,
  skipWelcomeEmail,
  email,
}: {
  lowercaseBlogUrl: string

  blogId: string

  // If subscription is coming from a recommendation, this is the blog ID and wallet of the blog that did the recommending
  referrer?: { id: string; wallet: string }

  // If we want to skip the welcome email (eg, if the user is mass-subscribing to Farcaster followers)
  skipWelcomeEmail?: boolean

  // Email only needs to be present if the user is NOT logged in
  email?: string
}): Promise<
  ErrorResponse | { sub: Subscription; nextState: PostSubscribeState } | null
> {
  try {
    const params = { skipWelcomeEmail: false }

    if (skipWelcomeEmail) {
      params.skipWelcomeEmail = skipWelcomeEmail
    }

    const utm = getUtmCookie(blogId)

    const response = await customFetch<
      | ErrorResponse
      | { sub: Subscription; nextState: PostSubscribeState }
      | null
    >(`${API}/${lowercaseBlogUrl}/subscribe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, utm, referrer, ...params }),
    })

    return response
  } catch (e) {
    console.error("Error subscribing user to blog:", e)
    return null
  }
}

/** Update blog with @param updateObject  */
export async function updateBlog(
  blogId: string,
  updateObject: Partial<Blog>
): Promise<void> {
  if (!blogId) throw "No blog ID provided when updating blog"

  const response = await customFetch<SuccessResponse | ErrorResponse>(
    `${API}/${blogId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updateObject),
    }
  )

  if (isErrorResponse(response)) {
    console.error("Blog update failed.", response)
    toast.error("Update failed.")
  }

  return
}

/**
 * Get suggested publications for the home feed.
 *
 * Note: While the back-end API supports pagination, the front-end does not as we don't yet have a need for it.
 */
export async function getSuggestedPublications(
  page?: string,
  forceIgnoreCookies?: boolean
): Promise<PaginatedPublicBlogResponseWithAuthorName | ErrorResponse> {
  // Get the logged-in user's ID. We do it here and not in the API
  // so that we can cache requests for a particular user for 24 hours.
  let userId
  if (!forceIgnoreCookies) {
    userId = await getServerOrClientCookies("userId")
  }

  // Construct the URL with pagination
  let url = `${API}/suggested`

  // Add query parameters
  const params = new URLSearchParams()
  if (userId) params.set("userId", userId)
  if (page) params.set("page", page)

  // Append params to URL if we have any
  if (params.toString()) {
    url += `?${params.toString()}`
  }

  // Fetch the suggested publications with a 24 hour revalidation period.
  const response = await customFetch<PaginatedPublicBlogResponseWithAuthorName>(
    url,
    { next: { revalidate: 24 * 60 * 60 } }, // 24 hours.
    {
      bypassCookies: true,
    }
  )

  if (isErrorResponse(response)) {
    console.error("Error getting suggested publications:", response.msg)
    return response
  }

  return response
}

/**
 * Get the subscribers count for a blog
 * @param blogId The ID of the blog
 * @returns An object containing subscriber counts or an ErrorResponse
 */
export async function getSubscribersCount(
  blogId: string
): Promise<SubscriberCountResp> {
  try {
    const response = await customFetch<SubscriberCountResp | ErrorResponse>(
      `${API}/${decodeURIComponent(blogId)}/subscribers/count`,
      {},
      {
        bypassCookies: true,
      }
    )

    if (isErrorResponse(response)) {
      console.error("Error fetching subscribers count:", response.msg)
      throw new Error(response.msg)
    }

    return response
  } catch (e) {
    APIError.handle404Error(e)
    // Return a default response if there's an error
    return {
      total: 0,
      active: 0,
      unsubscribed: 0,
      activeAndOptedIntoGeneral: 0,
    }
  }
}

/**
 * Get the subscribers count for a blog
 * @param blogId The ID of the blog
 * @returns An object containing subscriber counts or an ErrorResponse
 */
export async function getSubscribersCountBySlug(
  blogSlug: string
): Promise<SubscriberCountResp> {
  try {
    const response = await customFetch<SubscriberCountResp | ErrorResponse>(
      `${API}/slug/${decodeURIComponent(blogSlug)}/subscribers/count`,
      { next: { revalidate: 24 * 60 * 60 } }, // 1 day
      {
        bypassCookies: true,
      }
    )

    if (isErrorResponse(response)) {
      console.error("Error fetching subscribers count by slug:", response.msg)
      throw new Error(response.msg)
    }

    return response
  } catch (e) {
    APIError.handle404Error(e)
  }
}

/**
 * Adds an opt out to a subscription (removing the opt in).
 * @param subscriptionId The ID of the subscription from which we're adding an opt out to.
 * @param optOutId The ID of the community which we're opting out of.
 * @returns Response from API, either the updated subscription or an error.
 */
export async function addSubscriptionOptOut(
  subscriptionId: string,
  optOutId: string
) {
  try {
    const response = await customFetch<UserAndSubscription | ErrorResponse>(
      `${API}/subscriptions/${subscriptionId}/optoutid/${optOutId}`,
      { method: "POST" }
    )

    return response
  } catch (error) {
    console.error("Error adding subscription opt-out:", error)
    return { msg: "Error adding subscription opt-out" }
  }
}

/**
 * Adds a community opt in (also removing the opt out).
 * @param subscriptionId The ID of the subscription from which we're removing opt outs.
 * @param communityId The ID of the community which we're removing the opt out for.
 * @returns Response from API, either the updated subscription or an error.
 */
export async function removeSubscriptionOptOut(
  subscriptionId: string,
  communityId: string
) {
  try {
    const response = await customFetch<UserAndSubscription | ErrorResponse>(
      `${API}/subscriptions/${subscriptionId}/optoutid/${communityId}`,
      { method: "DELETE" }
    )

    return response
  } catch (error) {
    console.error("Error removing subscription opt-out:", error)
    return { msg: "Error removing subscription opt-out" }
  }
}

/**
 * Get the recommendations for a blog.
 * @param blogId The ID of the blog.
 * @returns An array of recommendations or an ErrorResponse.
 */
export async function getRecommendations(blogId: string) {
  const response = await customFetch<BlogRecommendation[] | ErrorResponse>(
    `${API}/${blogId}/recommendations`,
    {},
    {
      bypassCookies: true,
    }
  )

  return response
}

/**
 * Uploads a new logo image for the user's blog
 * @param file The image file to upload as the logo
 * @returns A boolean indicating success or failure
 */
export async function changeLogo(file: File): Promise<boolean> {
  try {
    const formData = new FormData()
    formData.append("file", file)

    const response = await customFetch<boolean>(`${API}/logo`, {
      method: "POST",
      body: formData,
      // Don't manually set Content-Type for FormData, the browser will set it with the correct boundary
    })

    return response
  } catch (error) {
    console.error("Error uploading logo:", error)
    toast.error("Failed to upload logo")
    return false
  }
}

/**
 * Removes the logo from the user's blog
 * @returns A boolean indicating success or failure
 */
export async function removeLogo(): Promise<boolean> {
  try {
    const response = await customFetch<boolean>(`${API}/logo`, {
      method: "DELETE",
    })

    return response
  } catch (error) {
    console.error("Error removing logo:", error)
    toast.error("Failed to remove logo")
    return false
  }
}

/**
 * Adds a custom domain to a blog (after they've purchased that ability via Stripe).
 * @param domain The domain to add.
 */
export async function addCustomDomain(
  domain: string
): Promise<boolean | ErrorResponse> {
  try {
    const response = await customFetch<boolean | ErrorResponse>(
      `${API}/domain`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ domain }),
      }
    )

    if (isErrorResponse(response)) {
      console.error("Error adding custom domain:", response.msg)
      return response
    }

    return response
  } catch (e) {
    const errMsg = "Caught error adding custom domain" + e
    console.error(errMsg)
    return { msg: errMsg }
  }
}

/**
 * Removes a custom domain from a blog.
 * @param domain The domain to remove.
 */
export async function removeCustomDomain(
  domain: string
): Promise<boolean | ErrorResponse> {
  try {
    const domainWithoutProtocol = removeHttpOrHttpsFromDomain(domain)

    const response = await customFetch<boolean | ErrorResponse>(
      `${API}/domain/${domainWithoutProtocol}`,
      { method: "DELETE" }
    )

    if (isErrorResponse(response)) {
      console.error("Error deleting custom domain:", response.msg)
      return response
    }

    return response
  } catch (e) {
    const errMsg = "Caught error removing custom domain" + e
    console.error(errMsg)
    return { msg: errMsg }
  }
}

/**
 * Verifies whether a custom domain has been successfuly registered with the DNS provider or not.
 */
export async function verifyCustomDomain(
  domain: string
): Promise<GetCustomDomainResponse | ErrorResponse> {
  const domainWithoutProtocol = removeHttpOrHttpsFromDomain(domain)

  const response = await customFetch<GetCustomDomainResponse | ErrorResponse>(
    `${API}/domain/${domainWithoutProtocol}`
  )

  if (isErrorResponse(response)) {
    console.error("Error verifying custom domain:", response.msg)
    return response
  }

  return response
}

/**
 * Creates a Stripe checkout session for purchasing a custom domain.
 * @param redirectUrl The URL to redirect to after successful payment
 * @returns The session ID if successful, or an error response
 */
export async function createCheckoutSessionForCustomDomain({
  redirectUrl,
}: {
  redirectUrl: string
}): Promise<{ sessionId: string } | ErrorResponse> {
  try {
    const response = await customFetch<{ sessionId: string } | ErrorResponse>(
      `/stripe/create-checkout-session-custom-domain`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ redirectUrl }),
      }
    )

    if (isErrorResponse(response)) {
      console.error(
        "Error creating checkout session for custom domain:",
        response.msg
      )
      return response
    }

    return response
  } catch (e) {
    const errMsg = "Error creating checkout session for custom domain: " + e
    console.error(errMsg)
    return { msg: errMsg }
  }
}

/**
 * Update recommendations for the logged-in user's blog.
 * @param recommendations Array of recommendations to update. Must pass them all in or this will delete any not in the array.
 * @returns Success or error response.
 */
export async function updateRecommendations(
  recommendations: RecommendationItem[]
): Promise<ErrorResponse | SuccessResponse> {
  try {
    const response = await customFetch<ErrorResponse | SuccessResponse>(
      `${API}/recommendations`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ recommendations }),
      }
    )

    if (isErrorResponse(response)) {
      console.error("Error updating recommendations:", response.msg)
    }

    return response
  } catch (e) {
    console.error("Unexpected error updating recommendations:", e)
    return { msg: `Error updating recommendations: ${e}` }
  }
}

/**
 * Add a new recommendation to the logged-in user's blog.
 * @param recommendation New recommendation to add.
 * @returns Success or error response.
 */
export async function addRecommendation(
  recommendation: Partial<RecommendationModalItem>
): Promise<ErrorResponse | SuccessResponse> {
  try {
    const response = await customFetch<ErrorResponse | SuccessResponse>(
      `${API}/recommendations`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ recommendation }),
      }
    )

    if (isErrorResponse(response)) {
      console.error("Error adding recommendation:", response.msg)
    }

    return response
  } catch (e) {
    console.error("Unexpected error adding recommendation:", e)
    return { msg: `Error adding recommendation: ${e}` }
  }
}

/**
 * Remove a recommendation from the logged-in user's blog
 * @param recommendation Recommendation to remove
 * @returns Success or error response
 */
export async function removeRecommendation(
  recommendation: RecommendationItem
): Promise<ErrorResponse | SuccessResponse> {
  try {
    const response = await customFetch<ErrorResponse | SuccessResponse>(
      `${API}/recommendations`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ recommendation }),
      }
    )

    if (isErrorResponse(response)) {
      console.error("Error removing recommendation:", response.msg)
    }

    return response
  } catch (e) {
    console.error("Unexpected error removing recommendation:", e)
    return { msg: `Error removing recommendation: ${e}` }
  }
}

/**
 * Update the links for the logged-in user's blog
 * @param links Array of links to update
 * @returns True on success, false on error
 */
export async function updateLinks(
  links: Array<Link>
): Promise<SuccessResponse | ErrorResponse> {
  try {
    const response = await customFetch<SuccessResponse | ErrorResponse>(
      `${API}/links`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ links }),
      }
    )

    return response
  } catch (error) {
    console.error("Error updating links:", error)
    return { msg: "Error updating links." }
  }
}
